type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type AdminUser = {
  uid: string;
  customClaims?: {
    acl?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type UsersState = {
  isLoading?: boolean;
  users?: Record<string, AdminUser>;
  error?: { code: string; [key: string]: unknown } | null;
  aclUpdateErrorsByUserId?: Record<
    string,
    { code: string; [key: string]: unknown }
  >;
  [key: string]: unknown;
};

export type AdminState = {
  users?: UsersState;
  [key: string]: unknown;
};

const initialState: AdminState = {};

// Removes a single key from an aclUpdateErrorsByUserId-shaped map without
// mutating the original object. Used instead of rest-destructuring so we
// don't need to reason about unused-rest-sibling lint exceptions.
const removeUserAclError = (
  aclUpdateErrorsByUserId: UsersState['aclUpdateErrorsByUserId'],
  userId: string
): NonNullable<UsersState['aclUpdateErrorsByUserId']> => {
  const errors = aclUpdateErrorsByUserId || {};
  if (!(userId in errors)) {
    return errors;
  }
  const next = { ...errors };
  delete next[userId];
  return next;
};

const usersReducer = (
  stateArg: UsersState | undefined,
  action: ReducerAction
): UsersState => {
  const state = stateArg || { isLoading: true };
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return { isLoading: true };
    }
    case 'USERS_LIST_IS_LOADING': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'USERS_LIST_FINISHED_LOADING': {
      const payload = action.payload || {};
      const users = payload.users || [];
      const usersById = users.reduce(
        (acc: Record<string, AdminUser>, u: AdminUser) => {
          return {
            ...acc,
            [u.uid]: u,
          };
        },
        {} as Record<string, AdminUser>
      );
      return {
        ...state,
        ...payload,
        users: usersById,
        // payload never carries isLoading (it's only ever list/pagination
        // data), so without this the isLoading: true set by
        // USERS_LIST_IS_LOADING would survive the merge below and the
        // loading spinner would never turn off.
        isLoading: false,
        // payload also never carries `error` - it's only ever set by the
        // sibling USERS_LIST_FINISHED_LOADING_WITH_ERROR case. Without this,
        // a stale error from a previous failed load would survive this
        // merge and keep the error screen showing even after a subsequent
        // load succeeds.
        error: null,
      };
    }
    case 'USERS_LIST_FINISHED_LOADING_WITH_ERROR': {
      const error = action.payload.error || { code: 'Unknown' };
      return {
        ...state,
        error,
        isLoading: false,
      };
    }
    // USER_ACL_IS_UPDATING carries only { type, userId } (no `acl` field yet)
    // - it must NOT fall through into USER_ACL_FINISHED_UPDATING's body below,
    // which destructures action.acl and writes it into customClaims. Falling
    // through here would prematurely clobber the user's real acl with
    // `undefined` before the update even completes. No UI currently reads a
    // per-user loading flag for this, so it's a genuine no-op until then.
    //
    // It DOES need to clear any previous aclUpdateErrorsByUserId entry for
    // this userId, though: starting a fresh update for a user should not
    // leave a stale error from a prior failed attempt still displayed once
    // this attempt resolves. Only touch the field (and only when there's
    // actually something to remove) so a no-op stays a true no-op.
    case 'USER_ACL_IS_UPDATING': {
      const { userId } = action;
      const hasError =
        Boolean(state.aclUpdateErrorsByUserId) &&
        userId in state.aclUpdateErrorsByUserId;
      if (!hasError) {
        return state;
      }
      return {
        ...state,
        aclUpdateErrorsByUserId: removeUserAclError(
          state.aclUpdateErrorsByUserId,
          userId
        ),
      };
    }
    case 'USER_ACL_FINISHED_UPDATING': {
      const { userId, acl } = action;
      const user = state.users[userId];
      const hasError =
        Boolean(state.aclUpdateErrorsByUserId) &&
        userId in state.aclUpdateErrorsByUserId;
      // A USERS_LIST_FINISHED_LOADING refresh can drop a user from
      // state.users while their ACL update is still in flight (pagination,
      // or the account was deleted concurrently). There's no user object left
      // to attach the update to, so bail out rather than crashing on
      // `user.customClaims` - but a successful write should still clear any
      // stale error left over from a previous failed attempt for this
      // userId, same as the normal path below.
      if (!user) {
        if (!hasError) {
          return state;
        }
        return {
          ...state,
          aclUpdateErrorsByUserId: removeUserAclError(
            state.aclUpdateErrorsByUserId,
            userId
          ),
        };
      }
      const customClaims = user.customClaims || {};
      return {
        ...state,
        // A successful update must also clear any error left over from a
        // previous failed attempt for this same user - otherwise a stale
        // error Snackbar can keep showing after a retry actually succeeds.
        ...(hasError
          ? {
              aclUpdateErrorsByUserId: removeUserAclError(
                state.aclUpdateErrorsByUserId,
                userId
              ),
            }
          : {}),
        users: {
          ...state.users,
          [user.uid]: {
            ...user,
            customClaims: {
              ...customClaims,
              acl,
            },
          },
        },
      };
    }
    case 'USER_ACL_FINISHED_UPDATING_WITH_ERROR': {
      const error = (action.payload || {}).error || { code: 'Unknown' };
      // Keyed per-userId (rather than a single top-level field) so that
      // concurrent/near-concurrent ACL updates for different users don't
      // clobber each other's error - each user's failure is tracked
      // independently.
      return {
        ...state,
        aclUpdateErrorsByUserId: {
          ...(state.aclUpdateErrorsByUserId || {}),
          [action.userId]: error,
        },
      };
    }
    case 'USER_ACL_UPDATE_ERROR_DISMISSED': {
      // Dismissing the error must clear it from the Redux store itself (not
      // just local component UI state) - otherwise remounting the component
      // (e.g. navigating away and back) re-reads the still-present error
      // from state and re-shows it even though the user already dismissed it.
      const { userId } = action;
      const hasError =
        Boolean(state.aclUpdateErrorsByUserId) &&
        userId in state.aclUpdateErrorsByUserId;
      if (!hasError) {
        return state;
      }
      return {
        ...state,
        aclUpdateErrorsByUserId: removeUserAclError(
          state.aclUpdateErrorsByUserId,
          userId
        ),
      };
    }
    default: {
      return state;
    }
  }
};

const adminReducer = (
  state: AdminState = initialState,
  action: ReducerAction
): AdminState => {
  switch (action.type) {
    default: {
      return {
        ...state,
        users: usersReducer(state.users, action),
      };
    }
  }
};

export default adminReducer;
